import ParserInline from 'markdown-it/lib/parser_inline';
import Renderer, { RenderRule } from 'markdown-it/lib/renderer';
import Ruler from 'markdown-it/lib/ruler';
import parse from 'html-react-parser';
import katex from 'katex';
import markdownit from 'markdown-it';
import hljs from 'highlight.js';
import * as IncrementalDOM from 'incremental-dom/dist/incremental-dom-cjs';
import markdownitIncrementalDOM from 'markdown-it-incremental-dom';
import markdownTextMath from 'markdown-it-texmath';
import { ErrorLabels, MarkdownItWithIncrementalDOM } from '../../models/types';
import { renderDfAlert } from '../../helpers/mosaicHTMLRenderHelpers';
import { isString } from '../../helpers/typeHelpers';
import { TABLE_WRAPPER_CLASS_NAME } from './hooks/useTableSearch/useTableSearch';
import { promptErrorTagRegex, trustedHtmlTagTagRegex } from '../../helpers/messageStreamHelper';
import { getClassNames } from '../../helpers/classHelpers';
import { apiBaseUrlSchema, danfossAssetsBaseUrl, downloadsUrlSchema, downloadApiUrls } from '../../constants/consts';
import EnvConfigHelper from '../../helpers/envConfigHelper';
import { trimChar } from '../../helpers/stringHelpers';
import { copyToClipboard } from '../../helpers/clipboardHelper';
import labels from '../../data/labels';
import { Icons } from '../icon/icons/material';

const COPY_ICON_SVG = `<span class="material-icons-outlined" style="font-size: 18px;">${Icons.copy}</span>`;
const CHECK_ICON_SVG = `<span class="material-icons-outlined" style="font-size: 18px;">${Icons.check}</span>`;

const resizableImageFormats = ['.jpg', '.jpeg', '.gif'];

const apiBaseUrl = trimChar(EnvConfigHelper.get('api-base-url', ''), ['/', ' ']);
const apiBaseUrlSchemas = [apiBaseUrlSchema];
const downloadsUrlSchemas = [downloadsUrlSchema];


export function addMarkdownDetectionRule(md: MarkdownItWithIncrementalDOM, ruleName: string, detectionRule: ParserInline.RuleInline, ruleOptions?: Ruler.RuleOptions) {
    try {
        md.inline.ruler.at(ruleName, detectionRule, ruleOptions);
    } catch (e: unknown) {
        if (e instanceof Error) {
            const parserRuleNotFound = e?.message?.includes('Parser rule not found:');
            if (parserRuleNotFound) md.inline.ruler.push(ruleName, detectionRule, ruleOptions);
        }
    }
}
export function addMarkdownRendererRule(md: MarkdownItWithIncrementalDOM, ruleName: string, renderRule: RenderRule) {
    md.renderer.rules[ruleName] = renderRule;
}


export function addMarkdownDetectionAndRenderRule(markdownitInstance: MarkdownItWithIncrementalDOM, ruleName: string, detectionRule: ParserInline.RuleInline, renderRule: RenderRule, ruleOptions?: Ruler.RuleOptions) {
    // Rule to detect the custom tag
    addMarkdownDetectionRule(markdownitInstance, ruleName, detectionRule, ruleOptions);

    // Rule to render the custom tag
    addMarkdownRendererRule(markdownitInstance, ruleName, renderRule);
}

export function containsMarkdownErrorTag(markdown: unknown) {
    if (!isString(markdown)) return null;
    return markdown.match(promptErrorTagRegex);
}

export function containsMarkdownTrustedHtmlTag(markdown: unknown) {
    if (!isString(markdown)) return null;
    return markdown.match(trustedHtmlTagTagRegex);
}

export function setupMarkdownRule({
    md,
    tag,
    ruleName,
    getMatches,
    renderTag,

}: {
    md: MarkdownItWithIncrementalDOM;
    ruleName: string;
    tag: keyof HTMLElementTagNameMap;
    renderTag: RenderRule;
    getMatches: (content: string) => RegExpMatchArray | null;

}) {
    const detectTag: ParserInline.RuleInline = (state, silent) => {
        // Check for "<" and early return if not found. Saves RegExp check
        if (state.src.charCodeAt(state.pos) !== 0x3c) return false;

        // Check for full error tag using RegExp
        const currentContent = state.src.slice(state.pos);
        const match = getMatches(currentContent);

        // Return if no match was found
        if (!match) return false;

        // If not silent, we push content to token to be processed
        // in the md.renderer.rules['RULE_NAME'] function
        if (!silent) {
            const token = state.push(ruleName, tag, 0);
            // We are only interested in the content of the error tag, not the tags themselves
            const errorContent = match[1];
            // Return if no content was found
            if (!errorContent) return false;
            // Push content to token
            token.content = match[1];
        }

        // Change the position of the parser to the end of the error tag
        state.pos += match[0].length;

        // Return true to indicate the token was handled
        return true;
    };

    return addMarkdownDetectionAndRenderRule(md, ruleName, detectTag, renderTag);
}

export function setupErrorMarkdownRules<ErrorKey extends string>(md: MarkdownItWithIncrementalDOM, errorsDict?: ErrorLabels<ErrorKey>, defaultError?: ErrorKey) {
    const ruleName = 'custom-error';

    const renderTag: RenderRule = (tokens, idx) => {
        const token = tokens[idx];
        const errorsLabels = errorsDict ?? {} as ErrorLabels<ErrorKey>;
        const defaultErrorKey = defaultError ?? "" as ErrorKey;
        try {
            const content: { message: string; code: string; } = JSON.parse(token.content);

            if (!content.code) throw new Error('Parsed error json did not contain message or code');
            const defaultError = errorsLabels[defaultErrorKey];
            const error = content.code !== defaultError ? errorsLabels[content.code as ErrorKey] : undefined;
            return renderDfAlert(error?.h ?? defaultError?.h ?? "Error", error?.d ?? `${defaultError?.d ?? ""} (${content.message ?? "-"})`.trim(), 'pt-2');
        } catch (error) {
            console.error('Something went wrong when parsing this error', error);
            return renderDfAlert('Parsing error occurred', 'We apologize for any inconvenience this may have causes. Please try again later.');
        }
    };

    return setupMarkdownRule({
        md,
        tag: 'df-alert',
        ruleName,
        getMatches: containsMarkdownErrorTag,
        renderTag,
    });
}
export function setupTrustedHtmlRules(md: MarkdownItWithIncrementalDOM) {
    const ruleName = 'custom-trusted-html';

    const renderTrustedHtml = (html?: string) => html ? `<div class="trusted-html">
            ${parse(html)}
        </div>` : '';

    const renderTag: RenderRule = (tokens, idx) => {
        const token = tokens[idx];

        try {
            return renderTrustedHtml(token.content);
        } catch (error) {
            console.error('Something went wrong when parsing this error', error);
            return renderDfAlert('Parsing error occurred', 'We apologize for any inconvenience this may have causes. Please try again later.');
        }
    };

    return setupMarkdownRule({
        md,
        tag: 'div',
        ruleName,
        getMatches: containsMarkdownTrustedHtmlTag,
        renderTag
    });
}



// Ensures that links open in a new tab
export const linkOpenRenderOverride = (disableLinkRender?: boolean) => {
    const rule: Renderer.RenderRule = (tokens, idx) => {
        //let target = "_blank";
        const attributesString = (tokens[idx].attrs || [])
            .map(([key, value]: string[]) => {

                if (key === 'href') {
                    // If link is to "#" or empty, remove the href attribute and style as
                    // regular <span>. Styled with "a:not([href])" in css ('./markdown.scss')
                    if (!value || value === '#') {
                        return '';
                    }

                    // If the link is not an absolute link, add the http prefix
                    // to ensure it will not open relative to the current page
                    if (value) {
                        value = handleApiRelativeUrls(value);
                        if (!['http', '/', '#'].some(prefix => value.startsWith(prefix))) {
                            value = `http://${value}`;
                        }
                    }

                    // // TODO: handle the case when there is a query param at the end of the PDF URL
                    // // For example https://assets.danfoss.com/documents/359566/AB440847594423en-000103.pdf#page=5
                    // // Could also be a query param
                    // // The file fileType check fails in the question
                    // const fileType = WHITELISTED_FILE_FORMATS.find(f => value!.split('?')[0].endsWith(f));
                    // if (value.startsWith(danfossAssetsBaseUrl) && fileType) {
                    //     value = "#" + createAssetActionLink(value, fileType as ObjectTypes);
                    //     //target = "_self";
                    // }
                    // else if (value.startsWith("#")) { // TODO: also check if it is a valid side panel link
                    //     //target = "_self";
                    // }
                }

                return `${key}="${value}"`;
            });
        //target="${target}" 
        if (disableLinkRender) return `<span class="disabled-link">`;
        return `<a rel="noopener noreferrer" ${attributesString}>`;
    };

    return rule;
};

export function addLinkOpenRenderOverride(md: MarkdownItWithIncrementalDOM, disableLinkRender?: boolean) {
    addMarkdownRendererRule(md, 'link_open', linkOpenRenderOverride(disableLinkRender));
}
export const linkCloseRenderOverride = (disableLinkRender?: boolean) => {
    const rule: Renderer.RenderRule = () => {
        return disableLinkRender ? '</span>' : '</a>';
    };
    return rule;
};
export function addLinkCloseRenderOverride(md: MarkdownItWithIncrementalDOM, disableLinkRender?: boolean) {
    addMarkdownRendererRule(md, 'link_close', linkCloseRenderOverride(disableLinkRender));
}


// Adds Mosaic table class to tables
// Adds overflow-x-scroll class to tables - class is in `Source/src/index.css`
// Also add additional class to table container so it could be targeted for table search functionality
export const TABLE_OVERFLOW_CLASS_NAME = 'overflow-x-scroll';
// Needed for overflowing the menu to the left of the table
export const TABLE_MENU_CLASS_NAME = 'menu-wrapper';
export const tableOpenRenderOverride: Renderer.RenderRule = () => `<div class="${TABLE_WRAPPER_CLASS_NAME}"><div class="${TABLE_MENU_CLASS_NAME}"><div class="${TABLE_OVERFLOW_CLASS_NAME}"><table class="df-table">`;
export function addTableOpenRenderOverride(md: MarkdownItWithIncrementalDOM) {
    addMarkdownRendererRule(md, 'table_open', tableOpenRenderOverride);
}


export const tableCloseRenderOverride: Renderer.RenderRule = () => '</table></div></div>';
export function addTableCloseRenderOverride(md: MarkdownItWithIncrementalDOM) {
    addMarkdownRendererRule(md, 'table_close', tableCloseRenderOverride);
}

export const IMAGE_WRAPPER_CLASS_NAME = 'markdown-image-container';

/**
 * Matches and validates that the input is a valid and complete image URL in one of the following formats:
 * 1. Image file URLs ending with .png, .jpg, .jpeg, or .webp (optionally followed by ?, #, =, or end of string)
 * 2. Data URIs for images of type PNG, JPG, JPEG, or WEBP in base64 encoding
 * 3. Blob URLs starting with blob:http(s)://, ensuring the URL is complete with a valid origin and a UUID-like identifier (at least 8 hex/dash characters)
 *
 * This regex ensures that only well-formed and complete image resource URLs are matched for use in web applications.
 */
const supportedImageFormatRegExp =
    /(\.(png|jpg|jpeg|webp)(?=\?|#|=|$))|(^data:image\/(png|jpg|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$)|(^blob:https?:\/\/[^\s/]+\/[a-f0-9-]{8,}$)/i;



const dimensionsRegExp = /#(\d+)?x(\d+)?$/;

export const handleApiRelativeUrls = (url: string) => {
    for (let i = 0; i < apiBaseUrlSchemas.length; i++) {
        const schema = apiBaseUrlSchemas[i];
        if (!url?.startsWith(schema))
            continue;
        const path = url.substring(schema.length);
        url = `${apiBaseUrl}/${trimChar(path, ['/', ' '])}`;
        return url;
    }
    for (let i = 0; i < downloadsUrlSchemas.length; i++) {
        const schema = downloadsUrlSchemas[i];
        if (!url?.startsWith(schema))
            continue;

        try {
            const u = new URL(url);
            const p = new URLSearchParams(u.search);
            const type = u.hostname;
            const id = p.get('id');
            if (type in downloadApiUrls && id)
                return downloadApiUrls[type as keyof typeof downloadApiUrls](id);

            throw new Error(`Unknown download type: ${type}`);
        }
        catch {
            return "";
        }
    }

    return url;
};
export const imageRenderOverride: (imageBaseUrl?: string) => Renderer.RenderRule = (imageBaseUrl) => (tokens, idx, options, env, self) => {
    const token = tokens[idx];

    const alt = token.content;
    let src = token.attrGet("src") || '';

    // Apply image resize (optimization for API asset scaling) 
    if (src && src.startsWith(danfossAssetsBaseUrl) && resizableImageFormats.some(f => src!.endsWith(f))) {
        src += "?width=600&height=300&mode=max";
    }

    // Apply base url to relative paths
    if (src.startsWith('.')) {
        src = src.substring(1);
        if (!src.startsWith('/')) src = `/${src}`;
    }
    if (src.startsWith('/')) {
        src = `${imageBaseUrl ?? window.location.origin}${src}`;
    }
    else {
        src = handleApiRelativeUrls(src);
    }
    // Parse dimensions from the alt text if present
    const dimensions = src.match(dimensionsRegExp);
    let width;
    let height;
    let isInvalid = false;

    // Check if the image is of a supported type
    if (!src.match(supportedImageFormatRegExp)) {
        isInvalid = true;
    }

    // Attach dimensions to the image
    if (dimensions) {
        width = dimensions[1] || '';
        height = dimensions[2] || '';
        src = src.replace(dimensionsRegExp, '').trim();
    }

    // Construct the image HTML tag
    // Unless the image source is valid, we do not want to set it in the source as the browser will attempt to load the image
    // This is to handle the streaming scenario
    let imgHtml = `<img class='markdown-image' alt="${alt}"`;
    if (width) imgHtml += ` width="${width}"`;
    if (height) imgHtml += ` height="${height}"`;
    if (isInvalid) {
        imgHtml += ` data-src="${src}"`;
    }
    else {
        imgHtml += ` src="${src}"`;
        imgHtml += ` onerror="if(!this.dataset.retry){this.dataset.retry=1;setTimeout(()=>{this.src='';this.src=this.src;},1000)}"`;
    }
    imgHtml += '/>';

    return `<span class='${getClassNames([IMAGE_WRAPPER_CLASS_NAME, isInvalid && 'image-load-error'])}'>${imgHtml}</span>`;
};
export function addImageProcessor(md: MarkdownItWithIncrementalDOM, imageBaseUrl?: string) {
    addMarkdownRendererRule(md, 'image', imageRenderOverride(imageBaseUrl));
}


const CODE_COPY_BUTTON_CLASS = 'markdown-it-code-copy';

export const codeBlockRenderOverride: (origRule: RenderRule) => RenderRule = (origRule) => (tokens, idx, options, env, self) => {
    const content = tokens[idx].content;
    const origRendered = origRule(tokens, idx, options, env, self);

    if (!content || content.length === 0) {
        return origRendered;
    }

    const escapedContent = content
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');

    const copyButton = `<button class="${CODE_COPY_BUTTON_CLASS}" data-clipboard-text="${escapedContent}" title="${labels.codeBlockCopyTitle}" onclick="window.handleCodeCopyClick(this)">
        <span class="icon-wrapper">
            ${COPY_ICON_SVG}
        </span>
        <span class="text-wrapper">${labels.codeBlockCopy}</span>
    </button>`;

    return `<div class="code-block-wrapper">${origRendered}${copyButton}</div>`;
};

export function addCodeBlockCopyButton(md: MarkdownItWithIncrementalDOM) {
    const originalCodeBlock = md.renderer.rules.code_block || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
    const originalFence = md.renderer.rules.fence || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

    addMarkdownRendererRule(md, 'code_block', codeBlockRenderOverride(originalCodeBlock));
    addMarkdownRendererRule(md, 'fence', codeBlockRenderOverride(originalFence));
}

export function setupCodeCopyEventListener() {
    window.handleCodeCopyClick = handleCodeCopyClick;
}

function handleCodeCopyClick(button: HTMLButtonElement) {
    if (button.dataset.copying === 'true') return;

    const code = button.getAttribute('data-clipboard-text');
    if (!code) return;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = code;
    const decodedCode = textarea.value;

    button.dataset.copying = 'true';

    copyToClipboard(decodedCode)
        .then(() => {
            const iconWrapper = button.querySelector('.icon-wrapper');
            const textWrapper = button.querySelector('.text-wrapper') as HTMLElement;

            button.style.opacity = '1';

            if (iconWrapper) {
                iconWrapper.innerHTML = CHECK_ICON_SVG;
            }
            if (textWrapper) {
                textWrapper.innerText = labels.codeBlockCopied;
            }

            setTimeout(() => {
                button.style.opacity = '';
                if (iconWrapper) {
                    iconWrapper.innerHTML = COPY_ICON_SVG;
                }
                if (textWrapper) {
                    textWrapper.innerText = labels.codeBlockCopy;
                }
                button.dataset.copying = 'false';
            }, 2000);
        })
        .catch(() => {
            button.title = labels.codeBlockCopyFailed;
            button.dataset.copying = 'false';
            setTimeout(() => {
                button.title = labels.codeBlockCopyTitle;
            }, 1500);
        });
}


type ErrorKey = string;
export function initializeMarkdownIt({ errorMessageDict, defaultError, imageBaseUrl, disableLinkRender }: { errorMessageDict?: ErrorLabels<ErrorKey>; defaultError?: ErrorKey; imageBaseUrl?: string; disableLinkRender?: boolean; }): MarkdownItWithIncrementalDOM {
    const md = markdownit({
        linkify: true,

        highlight: function (str: any, lang: any) {
            if (lang && hljs.getLanguage(lang)) {
                try {
                    return hljs.highlight(str, { language: lang }).value;
                } catch (__) {}
            }

            return ''; // use external default escaping
        }
    })
        .use(markdownTextMath, {
            engine: katex,
            delimiters: ['dollars', 'beg_end', 'brackets', 'doxygen', 'gitlab', 'julia'],
            katexOptions: { throwOnError: false }
        })
        .use(markdownitIncrementalDOM, IncrementalDOM) as MarkdownItWithIncrementalDOM;

    addTableOpenRenderOverride(md); // Apply mosaic table class
    addTableCloseRenderOverride(md); // Apply mosaic table class
    addLinkOpenRenderOverride(md, disableLinkRender); // Add target="_blank" to links
    addLinkCloseRenderOverride(md, disableLinkRender); // In some cases the markdown renderer crashes if we do not explicitly set the link_close rule
    addImageProcessor(md, imageBaseUrl); // Add image resize params for images from assets.danfoss.com
    addCodeBlockCopyButton(md); // Add copy button to code blocks
    setupTrustedHtmlRules(md);
    setupErrorMarkdownRules(md, errorMessageDict, defaultError);
    setupCodeCopyEventListener(); // Attach copy handler to window object
    return md;
}


// Async markdown wrapper = pass url in, handles: download and load/error/render
